import { useEffect } from 'react';

const SITE_NAME = 'vm2pod';
const DEFAULT_DESC = 'From VMs to Pods — Deep technical articles on VMware, Kubernetes, DevOps, and cloud-native architecture.';

export default function SEO({ title, description, image, url, type = 'website', article, noindex }) {
  const pageTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — From VMs to Pods`;
  const pageDesc = description || DEFAULT_DESC;
  const pageUrl = url || 'https://vm2pod.com';

  useEffect(() => {
    document.title = pageTitle;

    const setMeta = (name, content, prop = false) => {
      if (!content) return;
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', pageDesc);
    if (noindex) setMeta('robots', 'noindex,nofollow');

    // Open Graph
    setMeta('og:type', type, true);
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', pageDesc, true);
    setMeta('og:url', pageUrl, true);
    setMeta('og:site_name', SITE_NAME, true);
    if (image) setMeta('og:image', image, true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDesc);
    if (image) setMeta('twitter:image', image);

    // Article-specific
    if (article) {
      setMeta('article:published_time', article.publish_date || article.created_at, true);
      if (article.updated_at) setMeta('article:modified_time', article.updated_at, true);
      if (article.author?.name) setMeta('article:author', article.author.name, true);
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    // JSON-LD structured data
    let jsonLdEl = document.getElementById('seo-jsonld');
    if (!jsonLdEl) {
      jsonLdEl = document.createElement('script');
      jsonLdEl.id = 'seo-jsonld';
      jsonLdEl.type = 'application/ld+json';
      document.head.appendChild(jsonLdEl);
    }

    if (article) {
      jsonLdEl.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": article.title,
        "description": article.excerpt || pageDesc,
        "author": { "@type": "Person", "name": article.author?.name || 'vm2pod Author' },
        "publisher": { "@type": "Organization", "name": SITE_NAME },
        "datePublished": article.publish_date || article.created_at,
        "dateModified": article.updated_at || article.created_at,
        "wordCount": article.word_count,
        "timeRequired": `PT${article.read_time || 1}M`,
        "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl },
      });
    } else {
      jsonLdEl.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": SITE_NAME,
        "url": "https://vm2pod.com",
        "description": DEFAULT_DESC,
      });
    }

    return () => {
      document.title = `${SITE_NAME} — From VMs to Pods`;
    };
  }, [pageTitle, pageDesc, pageUrl, image, type, article, noindex]);

  return null;
}
